import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { 
  ArrowLeft, 
  Search, 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Loader2,
  Tag,
  Calendar,
  Eye,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const HelpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // States
  const [categorias, setCategorias] = useState([]);
  const [artigos, setArtigos] = useState([]);
  const [conteudoAtual, setConteudoAtual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showContent, setShowContent] = useState(false);

  const pageSize = 6;

  // Carregar categorias ao inicializar
  useEffect(() => {
    carregarCategorias();
    
    // Verificar se há código na URL
    const params = new URLSearchParams(location.search);
    const codigo = params.get('codigo');
    
    if (codigo) {
      carregarConteudoManual(codigo);
    } else {
      carregarNovidades(1, pageSize, null);
    }
  }, [location]);

  // Carregar categorias da API
  const carregarCategorias = async () => {
    try {
      const response = await axios.get('https://gatewaysistemas.com.br/GatewayApi/api/ManualCategoria/getAllCategoriasGeral');
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    }
  };

  // Carregar novidades/artigos
  const carregarNovidades = async (pagina, tamanho, pesquisa) => {
    setLoading(true);
    setShowContent(false);
    
    try {
      const searchParam = pesquisa === null || pesquisa === '' ? 'null' : pesquisa;
      const url = `https://gatewaysistemas.com.br/GatewayApi/api/ManualConteudo/getConteudosPaginacao?pageNo=${pagina}&pageSize=${tamanho}&search=${searchParam}`;
      
      const response = await axios.get(url);
      
      setArtigos(response.data);
      
      // Extrair informações de paginação dos headers
      const pageCount = response.headers['x-paging-pagecount'];
      const pageNo = response.headers['x-paging-pageno'];
      
      setTotalPages(parseInt(pageCount) || 0);
      setCurrentPage(parseInt(pageNo) || 1);
      
      setConteudoAtual(null);
    } catch (error) {
      console.error('Erro ao carregar novidades:', error);
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  // Carregar conteúdo específico
  const carregarConteudoManual = async (codigo) => {
    setLoading(true);
    
    try {
      const response = await axios.get(`https://gatewaysistemas.com.br/GatewayApi/api/ManualConteudo/getConteudoCodigo?codigo=${codigo}`);
      
      if (response.data && response.data.length > 0) {
        setConteudoAtual(response.data[0]);
        setShowContent(true);
        setArtigos([]);
        
        // Atualizar URL sem recarregar página
        window.history.pushState('', '', `#/ajuda?codigo=${codigo}`);
      }
    } catch (error) {
      console.error('Erro ao carregar conteúdo:', error);
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  // Pesquisar artigos
  const pesquisar = () => {
    const termo = searchTerm.trim() || null;
    carregarNovidades(1, pageSize, termo);
  };

  // Handle Enter na busca
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      pesquisar();
    }
  };

  // Navegar para página
  const navegarPagina = (pagina) => {
    const termo = searchTerm.trim() || null;
    carregarNovidades(pagina, pageSize, termo);
  };

  // Toggle categoria expandida
  const toggleCategoria = (index) => {
    setExpandedCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Voltar para listagem
  const voltarListagem = () => {
    setShowContent(false);
    setConteudoAtual(null);
    window.history.pushState('', '', window.location.pathname);
    carregarNovidades(1, pageSize, null);
  };

  // Render da árvore de categorias
  const renderCategoriaTree = (categoria, index) => (
    <div key={index} className="mb-2">
      <Collapsible open={expandedCategories[index]} onOpenChange={() => toggleCategoria(index)}>
        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 text-left hover:bg-gray-100 rounded-md">
          <span className="font-medium text-gray-700">{categoria.descricao}</span>
          {categoria.modulos && categoria.modulos.length > 0 && (
            expandedCategories[index] ? 
              <ChevronDown className="h-4 w-4" /> : 
              <ChevronRight className="h-4 w-4" />
          )}
        </CollapsibleTrigger>
        
        {categoria.modulos && categoria.modulos.length > 0 && (
          <CollapsibleContent className="pl-4 space-y-1">
            {categoria.modulos.map((modulo, moduloIndex) => (
              <div key={moduloIndex}>
                <div className="p-2 text-sm text-gray-600 font-medium">
                  {modulo.descricao}
                </div>
                {modulo.conteudo && modulo.conteudo.length > 0 && (
                  <div className="pl-4 space-y-1">
                    {modulo.conteudo.map((conteudo, conteudoIndex) => (
                      <button
                        key={conteudoIndex}
                        onClick={() => carregarConteudoManual(conteudo.codigo)}
                        className="block w-full text-left p-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        {conteudo.assunto}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  );

  // Render da paginação
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const current = currentPage;
    const total = totalPages;

    // Botão anterior
    if (current > 1) {
      pages.push(
        <Button
          key="prev"
          variant="outline"
          size="sm"
          onClick={() => navegarPagina(current - 1)}
        >
          &lt;
        </Button>
      );
    }

    // Primeira página
    pages.push(
      <Button
        key={1}
        variant={current === 1 ? "default" : "outline"}
        size="sm"
        onClick={() => navegarPagina(1)}
      >
        1
      </Button>
    );

    // Páginas intermediárias
    if (current > 3) {
      pages.push(<span key="dots1" className="px-2">...</span>);
    }

    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      if (i !== 1 && i !== total) {
        pages.push(
          <Button
            key={i}
            variant={current === i ? "default" : "outline"}
            size="sm"
            onClick={() => navegarPagina(i)}
          >
            {i}
          </Button>
        );
      }
    }

    if (current < total - 2) {
      pages.push(<span key="dots2" className="px-2">...</span>);
    }

    // Última página
    if (total > 1) {
      pages.push(
        <Button
          key={total}
          variant={current === total ? "default" : "outline"}
          size="sm"
          onClick={() => navegarPagina(total)}
        >
          {total}
        </Button>
      );
    }

    // Botão próximo
    if (current < total) {
      pages.push(
        <Button
          key="next"
          variant="outline"
          size="sm"
          onClick={() => navegarPagina(current + 1)}
        >
          &gt;
        </Button>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-8">
        {pages}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao Site
              </Button>
              <img 
                src="https://customer-assets.emergentagent.com/job_eda8ca4d-c957-4325-a9ab-0a26f5df1d41/artifacts/2jmiflv8_Gateway_500_200_2_C.png" 
                alt="Gateway Sistemas" 
                className="h-8 w-auto"
              />
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Categorias */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="mr-2 h-5 w-5" />
                  Categorias de Ajuda
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                {categorias.map((categoria, index) => renderCategoriaTree(categoria, index))}
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Search Bar */}
            {!showContent && (
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Pesquisar artigos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="flex-1"
                    />
                    <Button onClick={pesquisar} disabled={loading}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            )}

            {/* Content View */}
            {showContent && conteudoAtual && (
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <Button variant="ghost" onClick={voltarListagem} className="mb-4">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Voltar à listagem
                    </Button>
                  </div>
                  
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: conteudoAtual.conteudo }}
                  />
                  
                  {conteudoAtual.tags && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Tag className="h-4 w-4" />
                        <span>Tags:</span>
                        <span>{conteudoAtual.tags}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Articles List */}
            {!showContent && !loading && (
              <>
                <div className="space-y-6">
                  {artigos.length > 0 ? (
                    artigos.map((artigo, index) => (
                      <Card key={index} className="hover:shadow-md transition-shadow cursor-pointer" 
                            onClick={() => carregarConteudoManual(artigo.codigo)}>
                        <CardContent className="p-6">
                          <div className="flex gap-4">
                            {artigo.img && (
                              <div className="flex-shrink-0">
                                <img 
                                  src={`https://gatewaysistemas.com.br${artigo.img}`}
                                  alt={artigo.descricao}
                                  className="w-24 h-24 object-cover rounded-md"
                                />
                              </div>
                            )}
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                <Badge variant="secondary">
                                  {artigo.descricao_categoria}
                                </Badge>
                                <span>•</span>
                                <span>{artigo.descricao_modulo}</span>
                              </div>
                              
                              <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-blue-600">
                                {artigo.descricao}
                              </h3>
                              
                              <p className="text-gray-600 text-sm mb-3">
                                {artigo.assunto}
                              </p>
                              
                              <Button variant="link" className="p-0 h-auto text-blue-600">
                                Leia mais →
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          Nenhum artigo encontrado
                        </h3>
                        <p className="text-gray-500">
                          Tente ajustar sua pesquisa ou navegue pelas categorias.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Pagination */}
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;